import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Fuel, Truck, ShieldCheck, Radio, Building2, Landmark, ShoppingBag, GraduationCap, HeartPulse, Home, Hotel, Mountain, Factory, Fish, Snowflake, Wheat, MapPin, Phone, Mail, Newspaper, ArrowRight, UserPlus, MapPinned, CalendarClock, CreditCard, PackageCheck, ExternalLink, Clock, IndianRupee, PhoneCall, Users, Wallet, BellRing, Radar, Receipt, LocateFixed, Instagram, Facebook, Linkedin, Star } from 'lucide-react';
import { MEDIA_COVERAGE, MEDIA_CLIPPINGS } from '@/data/mediaCoverage';
import { ThemeToggle } from '@/components/ThemeToggle';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  { label: 'Home', href: '#home' },
  { label: 'How it works', href: '#how' },
  { label: 'Sector We Serve', href: '#sectors' },
  { label: "Benefit's", href: '#benefits' },
  { label: "FAQ's", href: '#faq' },
  { label: 'Service Location', href: '#location' },
  { label: 'Media Coverage', href: '#media' },
];

const BENEFITS = [
  { title: 'Save man power', icon: Users, desc: 'You don\u2019t need to assign work to your staff to go fuel station, You can redirect your staff to do various other jobs. Saving of man hours to make you more profitable.' },
  { title: 'Save Transportation cost', icon: Truck, desc: 'You are saving transportation cost when you go with Mobile fuel kart. It will observe that use of free delivery services save the 20% cost.' },
  { title: 'Save Fuel', icon: Fuel, desc: 'Wastage of fuel when delivery through traditional and unprofessional methods while Mobile fuel kart is Eliminates the chances of theft/spills/ignition.' },
  { title: 'Save money', icon: Wallet, desc: 'We Eliminate over billing , reduces diesel storage & inventory cost.' },
  { title: 'Instant alert', icon: BellRing, desc: 'As soon as fuel is delivered, instant alert is triggered by our servers in form of SMS/Email.' },
  { title: 'Live tracking', icon: LocateFixed, desc: 'Through Mobile fuel kart mobile application Know the exact location of your tanker while it travels to your destination!' },
  { title: 'Fuel dispensing by Geofence', icon: Radar, desc: 'We ensure that fuel is delivered only when our OSRV (Oil Spill Response Vessel) reaches the location.' },
  { title: 'Automated billing', icon: Receipt, desc: 'Digital printing leads to no human intervention! Get automated receipt upon delivery.' },
];

const SERVICE_AREAS = ['Noida', 'Greater Noida', 'Ghaziabad'];

const SOCIALS = [
  { id: 'instagram', label: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/mobile_fuel_kart?igsh=N2FvNHJqY2lramVi&utm_source=qr' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/share/1GWc6pfSfn/?mibextid=wwXIfr' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/in/binod-singh-0255442b?lutm_source=share_via&utm_content=profile&utm_medium=member_ios' },
];

const TRUST = [
  { id: 'licensed', icon: ShieldCheck, label: 'PESO Licensed Delivery' },
  { id: 'tracking', icon: MapPinned, label: 'Real-Time Tracking' },
  { id: 'availability', icon: Clock, label: '24/7 Availability' },
  { id: 'pricing', icon: IndianRupee, label: 'Transparent \u20B9/L Pricing' },
];

const STEPS = [
  { n: '01', icon: UserPlus, title: 'Sign Up / Order Now / Call Us', desc: 'OTP based login and sign up. Create your order details.' },
  { n: '02', icon: MapPinned, title: 'Site Inspection', desc: 'Our team will contact you and inspect the site and geofence the delivery location.' },
  { n: '03', icon: CalendarClock, title: 'Choose Delivery Slot & Quantity', desc: 'Get diesel delivered on your own schedule — select date, time & quantity.' },
  { n: '04', icon: CreditCard, title: 'Choose Payment Options', desc: 'We accept multiple payment options — choose any of them.' },
  { n: '05', icon: PackageCheck, title: 'Fuel Gets Delivered', desc: 'Your diesel gets delivered at your doorstep in the time slot you chose!' },
];

const SECTORS = [
  { icon: Radio, label: 'Mobile Towers' }, { icon: Building2, label: 'Construction Sites' },
  { icon: Landmark, label: 'Banks' }, { icon: ShoppingBag, label: 'Mall' },
  { icon: GraduationCap, label: 'Education Institute' }, { icon: HeartPulse, label: 'Hospital' },
  { icon: Home, label: 'Residential Societies' }, { icon: Hotel, label: 'Hotels' },
  { icon: Mountain, label: 'Mining Sites' }, { icon: Building2, label: 'Office Buildings' },
  { icon: Factory, label: 'Industry / Factories' }, { icon: Fish, label: 'Fisheries' },
  { icon: Snowflake, label: 'Cold Storage' }, { icon: Wheat, label: 'Agriculture' },
];

const FAQS = [
  { q: 'How do I place an order?', a: 'Sign up with our OTP-based login, create your order with your preferred date, time and quantity — or simply call our hotline and we will do it for you.' },
  { q: 'Is the quantity and quality guaranteed?', a: 'Yes. A quality & quantity test is performed prior to every delivery through OMC (Oil Marketing Company) outlets.' },
  { q: 'Is doorstep diesel delivery safe?', a: 'Absolutely. Our bowser is PESO-approved, and fuel is dispensed only after our vehicle reaches the geofenced delivery location.' },
  { q: 'Which payment options can I use?', a: 'We accept multiple payment options — choose whichever suits you when placing your order.' },
  { q: 'Can I track my delivery?', a: 'Yes. Track your tanker live, and receive an instant SMS/Email alert the moment fuel is delivered.' },
  { q: 'Will I get an invoice?', a: 'Yes. Billing is fully automated with digital printing — you receive an automated receipt/invoice upon delivery.' },
];

const WA_LINK = 'https://wa.me/917042722262?text=' + encodeURIComponent('Hi Mobile Fuel Kart! I need doorstep diesel delivery.');

// Official WhatsApp glyph (lucide has no WhatsApp icon)
const WhatsAppIcon = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export default function Login() {
  // Public rating summary (baseline 300+ @ 4.9 blended with real customer reviews)
  const [rating, setRating] = useState({ avg: 4.9, count: 312 });
  useEffect(() => {
    fetch(`${API_BASE}/api/reviews/public`).then((r) => r.json())
      .then((d) => { if (d && d.avg) setRating(d); })
      .catch(() => {});
  }, []);
  const nav = useNavigate();
  const goSignIn = () => nav('/signin');

  return (
    <div className="mfk-theme min-h-screen scroll-smooth bg-background text-foreground pb-[76px] md:pb-0">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto max-w-7xl px-4 h-16 flex items-center justify-between gap-4">
          <a href="#home" className="flex items-center gap-2 shrink-0" data-testid="landing-logo">
            <span className="bg-white rounded-lg px-2.5 py-1.5 shadow-sm border border-border/60"><img src="/assets/logo.png" alt="Mobile Fuel Kart" className="h-6" /></span>
          </a>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} data-testid={`nav-${n.href.slice(1)}`}
                className="px-3 py-2 rounded-md text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <a href="tel:+917042722262" className="hidden sm:inline-flex"><Button size="sm" variant="secondary" className="border border-border" data-testid="header-call-button"><PhoneCall className="h-4 w-4 mr-1.5" />+91 70427 22262</Button></a>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex" aria-label="Chat on WhatsApp"><Button size="sm" className="bg-[#25D366] text-white hover:bg-[#1ebe5b] px-3" data-testid="header-whatsapp-button"><WhatsAppIcon className="h-4 w-4" /></Button></a>
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" onClick={goSignIn} data-testid="nav-login-button">Login</Button>
          </div>
        </div>
      </header>

      {/* ABOVE THE FOLD — cinematic luxury hero */}
      <section id="home" className="relative scroll-mt-16 overflow-hidden lux-noise">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
          style={{ background: 'radial-gradient(1200px 600px at 20% 10%, hsl(var(--primary) / 0.18), transparent 60%), radial-gradient(900px 500px at 80% 30%, hsl(var(--accent) / 0.12), transparent 55%)' }} />
        <div className="absolute inset-0 pointer-events-none lux-grid-overlay" aria-hidden="true" />
        {/* floating ambient orbs */}
        <div className="absolute -top-10 right-[12%] h-48 w-48 rounded-full bg-primary/10 blur-3xl lux-floaty pointer-events-none" aria-hidden="true" />
        <div className="absolute top-40 right-[38%] h-32 w-32 rounded-full bg-accent/10 blur-2xl lux-floaty-slow pointer-events-none" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:py-20">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,58%)_minmax(0,42%)] lg:items-center">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full lux-glass px-3 py-1 text-xs font-semibold" data-testid="hero-badge-bpcl"><ShieldCheck className="h-3.5 w-3.5 text-primary" />BPCL Authorized Dealer</span>
                <span className="inline-flex items-center gap-1.5 rounded-full lux-glass px-3 py-1 text-xs font-semibold" data-testid="hero-badge-247"><Clock className="h-3.5 w-3.5 text-primary" />24/7 Emergency Response</span>
              </div>
              <h1 className="mt-6 font-luxe font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.04]" data-testid="hero-headline">
                On-Demand Diesel Delivery at <span className="text-primary">Your Location</span>.
              </h1>
              <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl" data-testid="hero-subhead">
                Our PESO-licensed fuel karts reach you across Noida, Greater Noida & Ghaziabad. Transparent ₹/Litre pricing, live tracking, digital invoice.
              </p>
              <div className="mt-5 flex items-center gap-2" data-testid="hero-rating-badge">
                <span className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-4 w-4 text-accent fill-accent" />)}
                </span>
                <span className="text-sm font-semibold" data-testid="hero-rating-value">{rating.avg}/5</span>
                <span className="text-sm text-muted-foreground" data-testid="hero-rating-count">from {rating.count}+ customer ratings</span>
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Button size="lg" className="min-h-12 px-6 text-base font-semibold bg-accent text-accent-foreground hover:bg-accent/90 shadow-[0_10px_30px_hsl(var(--accent)/0.30)] hover:-translate-y-[1px] transition-[transform,box-shadow,background-color] duration-200 active:scale-95" onClick={goSignIn} data-testid="hero-order-fuel-now-button">
                  <Fuel className="h-5 w-5 mr-2" />Order Fuel Now
                </Button>
                <a href="tel:+917042722262"><Button size="lg" variant="secondary" className="min-h-12 px-5 border border-border" data-testid="hero-call-button"><PhoneCall className="h-4 w-4 mr-2" />Call Us Now</Button></a>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp"><Button size="lg" className="min-h-12 px-5 bg-[#25D366] text-white hover:bg-[#1ebe5b]" data-testid="hero-whatsapp-button"><WhatsAppIcon className="h-5 w-5 mr-2" />WhatsApp</Button></a>
              </div>

              {/* Trust & safety badges */}
              <div className="mt-9 grid grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="trust-badges">
                {TRUST.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 rounded-xl bg-card/80 backdrop-blur-sm border border-border shadow-sm px-3 py-2.5" data-testid={`trust-badge-${t.id}`}>
                    <t.icon className="h-4 w-4 shrink-0 text-primary" />
                    <span className="text-xs font-medium leading-tight">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Cinematic 3D card stack (desktop) */}
            <div className="relative hidden lg:block [perspective:1200px]" aria-hidden="true" data-testid="hero-cinematic-stack">
              <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-primary/10 via-transparent to-accent/10 blur-xl" />
              <div className="relative lux-glass lux-sweep rounded-[1.6rem] p-3 [transform:rotateY(-7deg)_rotateX(2deg)] shadow-[0_24px_80px_-40px_rgba(2,6,23,0.55)]">
                <div className="overflow-hidden rounded-[1.15rem]">
                  <img src="/assets/fleet.webp" alt="" width="2000" height="1500" loading="eager"
                    className="w-full h-[340px] object-cover lux-dolly" />
                </div>
                <div className="flex items-center justify-between px-3 py-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Fleet Status</p>
                    <p className="text-sm font-semibold">PESO-Licensed Bowsers · Live GPS</p>
                  </div>
                  <span className="lux-metal-chip rounded-full px-3 py-1.5 text-xs font-semibold text-foreground/80">On Duty</span>
                </div>
              </div>
              {/* floating stat chips */}
              <div className="absolute -left-8 top-8 lux-glass rounded-2xl px-4 py-3 lux-floaty shadow-lg">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Response</p>
                <p className="font-display text-lg font-semibold tabular">24/7</p>
              </div>
              <div className="absolute -right-4 bottom-16 lux-glass rounded-2xl px-4 py-3 lux-floaty-slow shadow-lg">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Pricing</p>
                <p className="font-display text-lg font-semibold">₹/Litre Transparent</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fuel Kart in action — video */}
      <section className="bg-black border-y border-border" data-testid="hero-video-section">
        <video className="w-full max-h-[72vh] object-cover" autoPlay loop muted playsInline preload="metadata" poster="/assets/fuelkart-poster.jpg" data-testid="hero-video">
          <source src="/assets/fuelkart.mp4" type="video/mp4" />
          <source src="/assets/fuelkart.webm" type="video/webm" />
        </video>
      </section>

      {/* How it works */}
      <section id="how" className="relative scroll-mt-16 py-16 border-y border-border lux-noise" style={{ background: 'linear-gradient(135deg, hsl(var(--lux-surface)), hsl(var(--lux-teal-soft) / 0.55))' }}>
        <div className="relative mx-auto max-w-7xl px-4">
          <p className="text-xs font-semibold text-primary text-center uppercase tracking-[0.18em]">Process</p>
          <h2 className="font-luxe text-3xl font-semibold text-center mt-2">How It Works</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s) => (
              <div key={s.n} className="relative rounded-xl border border-border bg-card p-5 transition-[box-shadow,border-color] duration-300 hover:shadow-[0_24px_80px_-40px_rgba(2,6,23,0.55)] hover:border-primary/30" data-testid={`how-step-${s.n}`}>
                <span className="absolute right-3 top-3 lux-metal-chip rounded-full h-8 w-8 flex items-center justify-center font-display text-sm font-bold text-foreground/70">{s.n}</span>
                <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center"><s.icon className="h-5 w-5 text-primary" /></div>
                <h3 className="mt-3 font-semibold text-sm leading-snug">{s.title}</h3>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Watch us in action — full promo video (below How It Works) */}
      <section id="watch" className="scroll-mt-16 py-16 border-b border-border" data-testid="promo-video-section">
        <div className="mx-auto max-w-5xl px-4">
          <p className="text-xs font-semibold text-primary text-center uppercase tracking-[0.18em]">See It Live</p>
          <h2 className="font-luxe text-3xl font-semibold text-center mt-2">Watch Mobile Fuel Kart in Action</h2>
          <p className="text-center text-muted-foreground mt-2 max-w-2xl mx-auto text-sm">
            From order to doorstep refuelling — see how our PESO-licensed fuel karts deliver diesel safely at your site.
          </p>
          <div className="mt-8 rounded-2xl overflow-hidden border border-border bg-black shadow-[0_32px_100px_-48px_rgba(2,6,23,0.7)]">
            <video
              className="w-full aspect-video"
              controls
              playsInline
              preload="metadata"
              poster="/assets/fuelkart-cover.jpg"
              data-testid="promo-video"
            >
              <source src="https://customer-assets-jai6qajn.emergentagent.net/job_lubri-track-1/artifacts/spazyz2n_Mobile%20Fuel%20Kart%20v1.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Sectors We Serve */}
      <section id="sectors" className="scroll-mt-16 py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-luxe text-3xl font-semibold text-center">Sector We Serve</h2>
          <p className="text-center text-muted-foreground mt-2 max-w-2xl mx-auto">Reliable doorstep diesel delivery for every kind of business and site.</p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {SECTORS.map((s) => (
              <div key={s.label} className="rounded-xl border border-border bg-card p-4 flex flex-col items-center text-center gap-2 hover:border-primary/40 hover:bg-muted/50 transition-colors" data-testid={`sector-${s.label}`}>
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><s.icon className="h-5 w-5 text-primary" /></div>
                <span className="text-xs font-medium leading-tight">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefit's — content from mobilefuelkart.in */}
      <section id="benefits" className="scroll-mt-16 py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-luxe text-3xl font-semibold text-center">Benefit's</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="group rounded-xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-primary/30" data-testid={`benefit-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center transition-colors duration-200 group-hover:bg-primary/15">
                  <b.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mt-4 font-semibold text-sm transition-colors duration-200 group-hover:text-primary">{b.title}</h3>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Wall — real customer testimonials */}
      {(rating.recent || []).length > 0 && (
        <section id="reviews" className="relative scroll-mt-16 py-16 overflow-hidden lux-noise" style={{ background: 'linear-gradient(135deg, hsl(var(--lux-teal-soft) / 0.45), hsl(var(--lux-surface)))' }} data-testid="reviews-wall">
          <div className="absolute inset-0 pointer-events-none lux-grid-overlay opacity-50" aria-hidden="true" />
          <div className="relative mx-auto max-w-7xl px-4">
            <p className="text-xs font-semibold text-primary text-center uppercase tracking-[0.18em]">Testimonials</p>
            <h2 className="font-luxe text-3xl font-semibold text-center mt-2">What Our Customers Say</h2>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-4 w-4 text-accent fill-accent" />)}</span>
              <span className="text-sm font-semibold">{rating.avg}/5</span>
              <span className="text-sm text-muted-foreground">· {rating.count}+ ratings</span>
            </div>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {rating.recent.slice(0, 6).map((r, idx) => (
                <figure key={`${r.name}-${idx}`} className="lux-glass rounded-2xl p-6 flex flex-col transition-[box-shadow,transform] duration-300 hover:shadow-[0_24px_80px_-40px_rgba(2,6,23,0.55)] hover:-translate-y-0.5" data-testid={`testimonial-card-${idx}`}>
                  <span className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= r.rating ? 'text-accent fill-accent' : 'text-muted-foreground/30'}`} />)}
                  </span>
                  <blockquote className="mt-3 text-sm text-foreground/85 leading-relaxed flex-1">“{r.comment}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2.5">
                    <span className="lux-metal-chip rounded-full h-9 w-9 flex items-center justify-center font-display text-sm font-bold text-foreground/70">{(r.name || 'C').charAt(0)}</span>
                    <span>
                      <span className="block text-sm font-semibold">{r.name}</span>
                      <span className="block text-[11px] text-muted-foreground">Verified customer</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="scroll-mt-16 py-16 bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="font-display text-3xl font-bold text-center">FAQ's</h2>
          <p className="text-center text-muted-foreground mt-2">Answers to the questions we hear most often.</p>
          <div className="mt-8 rounded-xl border border-border bg-card px-4">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Service Location */}
      <section id="location" className="scroll-mt-16 py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-display text-3xl font-bold text-center">Diesel Delivery in Noida, Greater Noida &amp; Ghaziabad</h2>
          <p className="mt-3 text-center text-sm text-muted-foreground max-w-3xl mx-auto" data-testid="service-areas-intro">
            Mobile Fuel Kart is your local doorstep diesel delivery partner across Delhi NCR — order online and our
            PESO-licensed fuel karts refuel your DG sets, machinery, and fleets right at your site.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3" data-testid="service-areas">
            {SERVICE_AREAS.map((city) => (
              <span key={city} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium" data-testid={`service-area-${city.toLowerCase().replace(/\s+/g, '-')}`}>
                <MapPin className="h-4 w-4 text-primary" />{city}
              </span>
            ))}
          </div>
          {/* Local SEO: city-wise service copy */}
          <div className="mt-8 grid gap-5 md:grid-cols-3" data-testid="city-seo-cards">
            <div className="rounded-xl border border-border bg-card p-6" data-testid="city-card-noida">
              <h3 className="font-semibold">Diesel Delivery in Noida</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Doorstep diesel delivery across all Noida sectors — Sector 62, 63, 80, 95, NSEZ and the Noida-Greater Noida
                Expressway. Bulk diesel for generators (DG sets), industries, hospitals, malls and construction sites with
                GST tax invoice and live tracking.
              </p>
              <a href="/diesel-delivery/noida" className="mt-3 inline-block text-sm font-medium text-primary hover:underline" data-testid="city-more-noida">Diesel delivery in Noida →</a>
            </div>
            <div className="rounded-xl border border-border bg-card p-6" data-testid="city-card-greater-noida">
              <h3 className="font-semibold">Diesel Delivery in Greater Noida</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                On-demand diesel home delivery in Greater Noida &amp; Greater Noida West (Noida Extension) — Knowledge Park,
                Ecotech zones, Surajpur, Yamuna Expressway. Scheduled refuelling for construction equipment, RMC plants
                and housing societies.
              </p>
              <a href="/diesel-delivery/greater-noida" className="mt-3 inline-block text-sm font-medium text-primary hover:underline" data-testid="city-more-greater-noida">Diesel delivery in Greater Noida →</a>
            </div>
            <div className="rounded-xl border border-border bg-card p-6" data-testid="city-card-ghaziabad">
              <h3 className="font-semibold">Diesel Delivery in Ghaziabad</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Trusted bulk diesel supplier in Ghaziabad — Sahibabad Industrial Area, Raj Nagar Extension, Indirapuram
                and NH-9 corridor. 24/7 emergency diesel for factories, cold storage, tower cranes and fleet depots.
              </p>
              <a href="/diesel-delivery/ghaziabad" className="mt-3 inline-block text-sm font-medium text-primary hover:underline" data-testid="city-more-ghaziabad">Diesel delivery in Ghaziabad →</a>
            </div>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-6" data-testid="location-address">
              <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center"><MapPin className="h-5 w-5 text-primary" /></div>
              <h3 className="mt-3 font-semibold">Registered Office</h3>
              <p className="mt-1 text-sm text-muted-foreground">PP1, Mahamaya Flyover, Sector 95, Noida - 201301, Uttar Pradesh, India</p>
              <p className="mt-2 text-sm text-muted-foreground">Serving businesses across Delhi NCR & surrounding regions.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6" data-testid="location-phone">
              <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center"><Phone className="h-5 w-5 text-primary" /></div>
              <h3 className="mt-3 font-semibold">Hotline</h3>
              <a href="tel:+917042722262" className="mt-1 block text-sm text-primary font-medium">+91 70427 22262</a>
              <p className="mt-2 text-sm text-muted-foreground">Call us any time during business hours.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6" data-testid="location-email">
              <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center"><Mail className="h-5 w-5 text-primary" /></div>
              <h3 className="mt-3 font-semibold">Email</h3>
              <a href="mailto:admin@mobilefuelkart.in" className="mt-1 block text-sm text-primary font-medium break-all">admin@mobilefuelkart.in</a>
              <p className="mt-2 text-sm text-muted-foreground">Write to us for orders & enquiries.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      <section id="media" className="scroll-mt-16 py-16 bg-muted/40 border-t border-border">
        <div className="mx-auto max-w-7xl px-4">
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold">Media Coverage</h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl mx-auto">Featured across national and regional press — from The Times of India and ET Auto to News18 — for pioneering BPCL-backed doorstep diesel delivery.</p>
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-coverage">
            {[...MEDIA_COVERAGE, ...MEDIA_CLIPPINGS].map((m, i) => (
              <a key={m.url} href={m.url} target="_blank" rel="noopener noreferrer"
                className="group rounded-xl border border-border bg-card overflow-hidden transition-transform duration-200 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`media-item-${i}`} aria-label={`Read coverage on ${m.pub}`}>
                <div className="aspect-[4/3] bg-white flex items-center justify-center p-3">
                  <img src={m.img} alt={m.pub} loading="lazy" className="max-h-full max-w-full object-contain" />
                </div>
                <div className="flex items-center justify-between gap-1 border-t border-border px-3 py-2">
                  <span className="text-xs font-medium truncate">{m.pub}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-opacity duration-200 opacity-60 group-hover:opacity-100 group-hover:text-primary" />
                </div>
              </a>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
            <a href="https://www.mobilefuelkart.in/media-coverage.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary font-medium" data-testid="media-source-link"><Newspaper className="h-4 w-4" />View on mobilefuelkart.in</a>
            <a href="tel:+917042722262" className="inline-flex items-center gap-1 text-primary font-medium"><Phone className="h-4 w-4" />+91 70427 22262</a>
            <a href="mailto:admin@mobilefuelkart.in" className="inline-flex items-center gap-1 text-primary font-medium"><Mail className="h-4 w-4" />admin@mobilefuelkart.in</a>
          </div>
        </div>
      </section>

      <footer className="py-8 bg-secondary border-t border-border">
        <div className="mx-auto max-w-7xl px-4 pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2"><span className="bg-white rounded-lg px-2.5 py-1.5 border border-border/60"><img src="/assets/logo.png" alt="Mobile Fuel Kart" className="h-6" /></span></div>
          <p className="text-xs text-muted-foreground font-medium">On-Demand Doorstep Diesel & Lubricant Delivery · Just Order and Relax</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={`Follow us on ${s.label}`} title={s.label}
                  className="h-9 w-9 rounded-full border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/60 transition-colors duration-200"
                  data-testid={`social-${s.id}-link`}>
                  <s.icon className="h-[18px] w-[18px]" />
                </a>
              ))}
            </div>
            <a href="#home" className="inline-flex items-center gap-1 text-xs text-primary font-semibold">Back to top <ArrowRight className="h-3.5 w-3.5 -rotate-90" /></a>
          </div>
        </div>
      </footer>

      {/* Sticky mobile call bar — thumb-zone CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/95 backdrop-blur border-t border-border px-3 pt-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]" data-testid="sticky-callbar">
        <div className="flex gap-2">
          <a href="tel:+917042722262" className="flex-1"><Button className="w-full min-h-12 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sticky-call-now-button"><PhoneCall className="h-5 w-5 mr-2" />Call Us Now</Button></a>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp"><Button className="min-h-12 px-4 bg-[#25D366] text-white hover:bg-[#1ebe5b]" data-testid="sticky-whatsapp-button"><WhatsAppIcon className="h-5 w-5" /></Button></a>
          <Button className="min-h-12 px-4 bg-accent text-accent-foreground font-semibold hover:bg-accent/90" onClick={goSignIn} data-testid="sticky-order-now-button"><Fuel className="h-5 w-5 mr-1.5" />Order</Button>
        </div>
      </div>
    </div>
  );
}
